import { combineLatest } from 'rxjs';
import { environment } from 'src/environments/environment';

// ===============================================================
// STATIC API ENDPOINT CONSTANTS
// ===============================================================

// Authentication endpoints
export const AUTHENTICATE_ENDPOINT = `${environment.baseUrl}/token/authenticate`;
export const REFRESH_TOKEN_ENDPOINT = `${environment.baseUrl}/token/refresh`;
export const VALIDATE_TOKEN_ENDPOINT = `${environment.baseUrl}/token/validate`;
export const LOGOUT_ENDPOINT = `${environment.baseUrl}/token/logout`;
export const TOKEN_USER_ENDPOINT = `${environment.baseUrl}/token/user`;

// User management endpoints
export const USERS_TOS_VERIFY_ENDPOINT = `${environment.baseUrl}/users/tos/verify`;
export const PASSWORD_RESET_REQUEST_ENDPOINT = `${environment.baseUrl}/resetpassword/request`;
export const PASSWORD_RESET_RESET_ENDPOINT = `${environment.baseUrl}/resetpassword/reset`;
export const SIGNUP_ENDPOINT = `${environment.baseUrl}/signup`;

// ===============================================================
// DYNAMIC API ENDPOINT FUNCTIONS
// ===============================================================

// User management with company/user ID
export const USER_ACCEPT_TOS_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/users/${userId}/accept-tos`;

export const USER_GET_TOS_TERMS_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/users/${userId}/getterms`;

export const USER_SHOW_TOS_TERMS_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/users/${userId}/show-tos`;

export const USER_CHANGE_PASSWORD_ENDPOINT = (
  companyId: number,
  userId: number,
): string =>
  `${environment.baseUrl}/${companyId}/users/${userId}/change-password`;

export const USER_UPDATE_INFO_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/users/${userId}/info`;

export const USER_DEACTIVATE_ENDPOINT = (
  companyId: number,
  personId: string,
): string => `${environment.baseUrl}/${companyId}/users/${personId}/deactivate`;

export const USER_REACTIVATE_ENDPOINT = (
  companyId: number,
  personId: string,
): string => `${environment.baseUrl}/${companyId}/users/${personId}/reactivate`;

// Company/People endpoints
export const COMPANY_BORROWERS_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/internal/borrowers`;

export const COMPANY_PEOPLE_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/people`;

export const COMPANY_INVITES_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/invites`;

// Invitation endpoints
export const SEND_INVITATION_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/invitations/send`;

export const RESEND_INVITATION_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/invitations/resend`;

// Invitation endpoints
export const GET_INVITED_MEMBERS_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/invites/signup/members?companyId=${companyId}`;

// Unused
//export const INVITE_MEMBER_ENDPOINT = (companyId: number): string =>
//    `${environment.baseUrl}/${companyId}/invites/signup/member`;

export const INVITE_MEMBER_WITH_GROUP_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/invites/signup/member?inviteForm=1`;

export const RESEND_INVITE_MEMBER_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/invites/resend/member`;

// Wallet endpoints
export const USER_WALLETS_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/${userId}/wallets`;

export const USER_WALLET_DEFAULT_ENDPOINT = (
  companyId: number,
  userId: number,
  achWalletId: string,
): string =>
  `${environment.baseUrl}/${companyId}/${userId}/wallets/${achWalletId}/default`;

export const USER_WALLET_DELETE_ENDPOINT = (
  companyId: number,
  userId: number,
  achWalletId: string,
): string =>
  `${environment.baseUrl}/${companyId}/${userId}/wallets/${achWalletId}/delete`;

export const USER_WALLET_UPDATE_ENDPOINT = (
  companyId: number,
  userId: number,
  achWalletId: string,
): string =>
  `${environment.baseUrl}/${companyId}/${userId}/wallets/${achWalletId}`;

// Transaction and Authorization endpoints
export const TRANSACTION_CAPTURE_ENDPOINT = (
  companyId: number,
  authorizationId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/capture/${authorizationId}`;

export const TRANSACTION_VOID_ENDPOINT = (
  companyId: number,
  authorizationId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/void/${authorizationId}`;

export const TRANSACTION_REFUND_ENDPOINT = (
  companyId: number,
  authorizationId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/refund/${authorizationId}`;

export const TRANSACTION_REFUND_LOANLEVEL_ENDPOINT = (
  companyId: number,
  authorizationId: string,
  acctrefno: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/refund/${authorizationId}/loan/${acctrefno}`;

export const TRANSACTION_EVENTS_ENDPOINT = (
  companyId: number,
  role: 'borrower' | 'supplier',
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/events/${role}`;

export const NLS_TRANSACTIONS_ENDPOINT = (
  companyId: number,
  role: 'borrower' | 'supplier',
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/${role}`;

export const AUTHORIZATION_BY_COMPANY_ENDPOINT = (
  companyId: number,
  role: 'borrower' | 'supplier',
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/authorization/${role}`;

export const AUTHORIZATION_HANDLE_ENDPOINT = (
  companyId: number,
  requestId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/authorization/${requestId}`;

/** Transaction Authorization Agreement (TAA) – HTML for a transaction request by its ID */
export const AUTHORIZATION_TAA_ENDPOINT = (
  companyId: number,
  transactionRequestId: string,
  selectedPlan?: number,
): string => {
  const base = `${environment.baseUrl}/${companyId}/authorization/taa/${transactionRequestId}`;
  return selectedPlan != null ? `${base}?selectedPlan=${selectedPlan}` : base;
};

// Transaction Request endpoints
export const REQUEST_CREATE_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/checkout/request/supplier`;

export const REQUEST_CANCEL_ENDPOINT = (
  companyId: number,
  requestId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/checkout/request/${requestId}/cancel`;

export const REQUESTS_BY_COMPANY_ENDPOINT = (
  companyId: number,
  role: 'borrower' | 'supplier',
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/checkout/${role}`;

// Customer and Credit endpoints
export const SUPPLIER_PLANS_ENDPOINT = (
  companyId: number,
  supplierId: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/plans/supplier/${supplierId}`;

export const CREDIT_SUMMARY_ENDPOINT = (
  companyId: number,
  companyToCheckId: number,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/internal/credit-summary/${companyToCheckId}`;

// Transaction Details endpoints
export const TRANSACTION_DETAILS_ENDPOINT = (
  companyId: number,
  loanNumber: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transaction-details/${loanNumber}/details`;

export const TRANSACTION_DETAILS_PENDING_PAYMENTS_ENDPOINT = (
  companyId: number,
  loanNumber: string,
): string =>
  `${environment.baseUrl}/${companyId}/commandcenter/transaction-details/${loanNumber}/pending-payments`;

/** Transaction Authorization Agreement (TAA) – pre-rendered HTML for an NLS transaction */
export const TRANSACTION_TAA_ENDPOINT = (
  companyId: number,
  transactionId: number,
): string =>
  `${environment.baseUrl}/${companyId}/transaction/taa/${transactionId}`;

// Signup and Password Reset with GUID
export const SIGNUP_BY_GUID_ENDPOINT = (guid: string): string =>
  `${environment.baseUrl}/signup/${guid}`;

export const PASSWORD_RESET_RECORD_ENDPOINT = (guid: string): string =>
  `${environment.baseUrl}/resetpassword/record/${guid}`;

// Ach payment endpoints
export const ACH_SUBMIT_PAYMENT_ENDPOINT = (
  companyId: number,
  loanNumber: string,
  achWalletId: string,
): string =>
  `${environment.baseUrl}/${companyId}/loans/${loanNumber}/achs/${achWalletId}`;

export const ACH_DELETE_PAYMENT_ENDPOINT = (
  companyId: number,
  loanNumber: string,
  rowId: string,
): string =>
  `${environment.baseUrl}/${companyId}/transactions/${loanNumber}/pending-payments/remove/${rowId}`;

export const ACH_UPDATE_PAYMENT_ENDPOINT = (
  companyId: number,
  loanNumber: string,
  rowId: string,
): string =>
  `${environment.baseUrl}/${companyId}/transactions/${loanNumber}/pending-payments/update/${rowId}`;

// Application endpoints
export const APPLICATION_BORROWERS_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/applications/borrowerapps`;

export const USER_LINKS_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/user-links/all`;

// Bank account endpoints
export const BANK_PLAID_ACCOUNTS_ENDPOINT = (
  companyId: number,
  userId: number,
): string => `${environment.baseUrl}/${companyId}/${userId}/plaid/accounts`;

// File upload endpoint
export const FILE_UPLOAD_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/upload`;

export const FILE_DOWNLOAD_ENDPOINT = (
  companyId: number,
  fileId: string,
): string => `${environment.baseUrl}/${companyId}/download/${fileId}`;

export const FILE_BATCH_DOWNLOAD_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/download/batch`;

// User group endpoints
export const USER_GROUPS_ADD_GROUP_TO_USER = (
  companyId: number,
  groupId: string,
  userId: string,
): string =>
  `${environment.baseUrl}/${companyId}/groups/${groupId}/users/${userId}`;

export const USER_GROUPS_ADD_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/groups`;

export const USER_GROUPS_PUT_ENDPOINT = (
  companyId: number,
  groupId: string,
): string => `${environment.baseUrl}/${companyId}/groups/${groupId}`;

export const USER_GROUPS_GET_ENDPOINT = (companyId: number): string =>
  `${environment.baseUrl}/${companyId}/groups`;

export const PERMISSIONS_ENDPOINT = (companyId: number, role: string): string =>
  `${environment.baseUrl}/${companyId}/permissions/${role}`;

export const TRANSACTION_EVENT_DETAIL_ENDPOINT = (
  companyId: number | string,
  eventId: string,
) =>
  `${environment.baseUrl}/${companyId}/commandcenter/transactions/events/${eventId}`;
